import { ArrowRight, ChevronLeft, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { apiClient } from '@/api/client'
import { useApiKeys } from '@/api/hooks/use-api-keys'
import { useDomains } from '@/api/hooks/use-domains'
import { StampIcon } from '@/components/ui/stamp-icon'
import { useAuth } from '@/lib/auth-context'
import { StepApiKeyFull } from './step-api-key-full'
import { StepDomainFull } from './step-domain-full'
import { StepInstallSdk } from './step-install-sdk'
import { StepSandboxSend } from './step-sandbox-send'
import { StepVerifyEmail } from './step-verify-email'

/**
 * Linear onboarding flow:
 *
 *   0: verify email (skipped if already verified)
 *   1: welcome splash
 *   2: send a test email (sandbox — can skip)
 *   3: install SDK (can skip)
 *   4: verify domain (can skip)
 *   5: API key → finish
 *
 * Progress dots show on steps 2–5 as "Step 1 of 4" through "Step 4 of 4".
 */

/**
 * Determines the first incomplete onboarding step based on existing state.
 * Returns -1 when all steps are complete (should mark onboarding finished).
 */
export function calculateStartStep(
  emailVerified: boolean,
  hasSentEmail: boolean,
  hasVerifiedDomain: boolean,
  hasPendingDomain: boolean,
  hasLiveApiKey: boolean,
): number {
  if (!emailVerified) return 0
  if (!hasSentEmail) return 2 // skip welcome, go straight to send
  if (!hasVerifiedDomain && !hasPendingDomain) return 4 // need to add domain
  if (hasPendingDomain && !hasVerifiedDomain) return 4 // show DNS records for pending domain
  if (!hasLiveApiKey) return 5 // need API key
  return -1 // all done — should mark complete
}

const PROGRESS_STEPS = 4 // steps 2–5

function readSession<T>(key: string, fallback: T): T {
  try {
    const v = sessionStorage.getItem(key)
    return v !== null ? (JSON.parse(v) as T) : fallback
  } catch {
    return fallback
  }
}

function writeSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* quota */
  }
}

/* ── Back button (renders inside card, top-left) ─────────────── */

function BackButton({ onClick }: { readonly onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
    >
      <ChevronLeft className="h-4 w-4" />
      Back
    </button>
  )
}

/* ── Progress dots (steps 2–5 mapped to 1–4) ────────────────── */

function ProgressDots({ step }: { readonly step: number }): React.JSX.Element {
  const idx = step - 2 // step 2 → 0, step 5 → 3
  return (
    <div className="mt-6 flex items-center justify-end gap-3">
      <span className="text-xs text-text-tertiary">
        Step {idx + 1} of {PROGRESS_STEPS}
      </span>
      <div className="flex gap-1.5">
        {Array.from({ length: PROGRESS_STEPS }, (_, i) => `dot-${i}`).map((id, i) => (
          <div
            key={id}
            className={
              i === idx
                ? 'h-2 w-6 rounded-full bg-brand-500 transition-all duration-300'
                : i < idx
                  ? 'h-2 w-2 rounded-full bg-brand-300 transition-all duration-300'
                  : 'h-2 w-2 rounded-full bg-surface-3 transition-all duration-300'
            }
          />
        ))}
      </div>
    </div>
  )
}

/* ── Main flow ───────────────────────────────────────────────── */

export function OnboardingFlow(): React.JSX.Element {
  const { user, account } = useAuth()
  const domainsQuery = useDomains()
  const apiKeysQuery = useApiKeys()

  const queriesLoading = domainsQuery.isLoading || apiKeysQuery.isLoading
  const resolvedRef = useRef(false)

  const [step, setStepRaw] = useState(() => {
    const saved = readSession<number>('oe_onboarding_step', -1)
    if (saved >= 0) return saved
    if (user?.emailVerified === true || user?.emailVerified === undefined) return 1
    return 0
  })

  function setStep(s: number): void {
    writeSession('oe_onboarding_step', s)
    setStepRaw(s)
  }

  async function handleFinish(): Promise<void> {
    try {
      await apiClient.put('/dashboard/account', {
        onboarding_completed: true,
        onboarding_steps: { test_email: true, sdk: true, domain: true, api_key: true },
      })
    } catch (err) {
      console.error('[onboarding] Failed to mark onboarding complete:', err)
    }
    sessionStorage.removeItem('oe_onboarding_step')
    sessionStorage.removeItem('oe_onboarding_domain_id')
    window.location.href = '/'
  }

  // Once queries resolve, compute the smart start step and override if needed
  useEffect(() => {
    if (queriesLoading || resolvedRef.current) return
    resolvedRef.current = true

    const emailVerified = user?.emailVerified ?? false
    const hasSentEmail = account?.onboarding_steps?.test_email === true
    const domains = domainsQuery.data?.data ?? []
    const hasVerifiedDomain = domains.some((d) => d.status === 'active')
    const hasPendingDomain = domains.some((d) => d.status === 'pending')
    const apiKeys = apiKeysQuery.data?.data ?? []
    const hasLiveApiKey = apiKeys.some((k) => k.type === 'live')

    const smartStep = calculateStartStep(
      emailVerified,
      hasSentEmail,
      hasVerifiedDomain,
      hasPendingDomain,
      hasLiveApiKey,
    )

    if (smartStep === -1) {
      handleFinish()
      return
    }

    // Override if the saved session step points to something already completed
    if (smartStep > step) {
      setStep(smartStep)
    }
  }, [queriesLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  if (queriesLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 lg:p-10">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    )
  }

  return (
    <div className="flex flex-1 items-start justify-center overflow-y-auto p-6 lg:p-10">
      <div className="w-full max-w-[860px]">
        {/* Step 0: Verify Email */}
        {step === 0 && (
          <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
            <StepVerifyEmail onVerified={() => setStep(1)} />
          </div>
        )}

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="flex flex-col items-center text-center py-8">
            <StampIcon size={56} variant="solid" />
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-text-primary">
              Welcome to One-Email
            </h1>
            <p className="mt-3 max-w-md text-text-secondary">
              Fast, reliable email for developers. Let&apos;s get you set up in a few quick steps.
            </p>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="mt-8 flex items-center gap-2 rounded-xl bg-brand-500 px-8 py-3.5 text-sm font-semibold text-white
                hover:bg-brand-600 transition-colors shadow-[0_4px_16px_rgba(255,107,0,0.2)]"
            >
              Get started
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Step 2: Send a test email */}
        {step === 2 && (
          <>
            <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
              <BackButton onClick={() => setStep(1)} />
              <StepSandboxSend onNext={() => setStep(3)} onSetupDomain={() => setStep(4)} />
            </div>
            <ProgressDots step={2} />
          </>
        )}

        {/* Step 3: Install SDK */}
        {step === 3 && (
          <>
            <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
              <BackButton onClick={() => setStep(2)} />
              <StepInstallSdk onNext={() => setStep(4)} onSkip={() => setStep(4)} />
            </div>
            <ProgressDots step={3} />
          </>
        )}

        {/* Step 4: Verify Domain */}
        {step === 4 && (
          <>
            <BackButton onClick={() => setStep(3)} />
            <StepDomainFull onNext={() => setStep(5)} onSkip={() => setStep(5)} />
            <ProgressDots step={4} />
          </>
        )}

        {/* Step 5: API Key */}
        {step === 5 && (
          <>
            <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
              <BackButton onClick={() => setStep(4)} />
              <StepApiKeyFull onFinish={handleFinish} />
            </div>
            <ProgressDots step={5} />
          </>
        )}
      </div>
    </div>
  )
}
